import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import type { CalendarItem } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { buildMonthGrid, localDayKey, WEEKDAYS } from '../../lib/calendarGrid';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ErrorState, LoadingState } from '../../components/ui/States';
import { cn } from '../../lib/cn';
import { CalendarPostCard } from './CalendarPostCard';

const MAX_PER_DAY = 3;

/**
 * Month-grid calendar for the Posts hub — the main "Calendar" layout. Each day cell shows mini-cards
 * (platform icon · time · title · media thumbnail) for its scheduled/published posts. Clicking a
 * card opens that post in the composer. `filterFn` (optional) narrows the items client-side (the
 * Calendar tab's Status / Post Type / Platform chips) — the month's data is fetched once ('all').
 */
export function PostsCalendar({
  tenantKey,
  filterFn,
}: {
  tenantKey: string;
  filterFn?: (item: CalendarItem) => boolean;
}) {
  const [params, setParams] = useSearchParams();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  /**
   * Day cells the user expanded past MAX_PER_DAY, keyed by ISO date. Deliberately component state:
   * it is a transient "let me see the rest of this day" gesture, not a preference — so it resets on
   * reload, and is cleared on month change so a day doesn't stay expanded behind your back.
   */
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const toggleDay = (iso: string) =>
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(iso)) next.delete(iso);
      else next.add(iso);
      return next;
    });

  const grid = useMemo(() => buildMonthGrid(year, month), [year, month]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['calendar', tenantKey, grid.from, grid.to, 'all'],
    queryFn: ({ signal }) => api.getCalendar(grid.from, grid.to, 'all', signal),
    enabled: tenantKey !== '',
  });

  const openPost = (postId: string) => {
    const next = new URLSearchParams(params);
    next.set('compose', postId);
    setParams(next);
  };

  // Group by LOCAL day (the grid cells are local dates), earliest first — after the optional
  // client-side filter (Status / Post Type / Platform).
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarItem[]>();
    const items = filterFn ? (data ?? []).filter(filterFn) : (data ?? []);
    for (const item of items) {
      const key = localDayKey(item.scheduledAt);
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
    return map;
  }, [data, filterFn]);

  const monthLabel = new Date(year, month).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
  const step = (delta: number) => {
    setExpandedDays(new Set());
    const next = new Date(year, month + delta);
    setYear(next.getFullYear());
    setMonth(next.getMonth());
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-base font-semibold text-ink">{monthLabel}</h2>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => step(-1)} aria-label="Previous month">
            ‹
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setExpandedDays(new Set());
              setYear(today.getFullYear());
              setMonth(today.getMonth());
            }}
          >
            Today
          </Button>
          <Button variant="secondary" size="sm" onClick={() => step(1)} aria-label="Next month">
            ›
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        {isError ? (
          <ErrorState
            message={error instanceof ApiRequestError ? error.message : 'Could not load calendar.'}
            onRetry={() => void refetch()}
          />
        ) : (
          <div className="relative">
            {isLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/60">
                <LoadingState label="Loading calendar…" />
              </div>
            )}
            <div className="grid grid-cols-7 border-b border-line-soft bg-page text-xs font-semibold uppercase tracking-wide text-ink-muted">
              {WEEKDAYS.map((d) => (
                <div key={d} className="px-2 py-2 text-center">
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {grid.days.map((cell) => {
                const items = byDay.get(cell.iso) ?? [];
                const expanded = expandedDays.has(cell.iso);
                const hidden = items.length - MAX_PER_DAY;
                return (
                  <div
                    key={cell.iso}
                    className={cn(
                      'min-h-32 border-b border-r border-line-soft p-1.5 align-top',
                      !cell.inMonth && 'bg-page/40',
                    )}
                  >
                    <div className="mb-1 px-0.5">
                      <span
                        className={cn(
                          'inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-medium',
                          cell.isToday
                            ? 'bg-action text-action-fg'
                            : cell.inMonth
                              ? 'text-ink-label'
                              : 'text-ink-ghost',
                        )}
                      >
                        {cell.date.getDate()}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {(expanded ? items : items.slice(0, MAX_PER_DAY)).map((item) => (
                        <CalendarPostCard key={item.id} item={item} onOpen={() => openPost(item.postId)} />
                      ))}
                      {hidden > 0 && (
                        <button
                          type="button"
                          onClick={() => toggleDay(cell.iso)}
                          aria-expanded={expanded}
                          className="focus-ring w-full rounded px-1 py-0.5 text-left text-[0.6875rem] font-medium text-ink-faint hover:text-link"
                        >
                          {expanded ? 'Show less' : `+${hidden} more`}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </>
  );
}
